import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Heading,
  Stack,
  Text,
  Button,
  Spinner,
  HStack,
  Tabs,
  TabList,
  Tab,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import TopMenu from "./TopMenu";
import PageGuide from "./components/PageGuide";
import {
  fetchNotifications,
  markRead,
  markAllRead,
  linkForNotification,
} from "./services/notificationsService";

export default function Notifications({ onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0); // 0 = all, 1 = unread
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchNotifications({
        unreadOnly: tab === 1,
        limit: 100,
      });
      setItems(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const onClickItem = async (n) => {
    if (!n.is_read) {
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)));
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await markRead(n.id);
      } catch {
        /* ignore */
      }
    }
    const link = linkForNotification(n);
    if (link && link !== "/notifications") {
      navigate(link);
    }
  };

  const onMarkAll = async () => {
    try {
      await markAllRead();
      load();
    } catch {
      /* ignore */
    }
  };

  return (
    <Box bg="surface.canvas" minH="100vh">
      <TopMenu onLogout={onLogout} />
      <Box maxW="900px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <Stack spacing={6}>
          <HStack justify="space-between" align="center">
            <HStack spacing={2} align="center">
              <Heading size="lg">Notifications</Heading>
              <PageGuide id="notifications" />
            </HStack>
            {unreadCount > 0 && (
              <Button size="sm" variant="outline" onClick={onMarkAll}>
                Mark all read
              </Button>
            )}
          </HStack>

          <Tabs index={tab} onChange={setTab}>
            <TabList>
              <Tab>All</Tab>
              <Tab>
                Unread{" "}
                {unreadCount > 0 && (
                  <Badge ml={2} colorScheme="red" borderRadius="full">
                    {unreadCount}
                  </Badge>
                )}
              </Tab>
            </TabList>
          </Tabs>

          {loading && <Spinner />}
          {error && <Text color="red.500">{error}</Text>}
          {!loading && !error && items.length === 0 && (
            <Text color="text.muted">No notifications.</Text>
          )}

          {!loading && !error && items.length > 0 && (
            <Stack
              spacing={0}
              border="1px solid"
              borderColor="border.default"
              borderRadius="md"
              divider={
                <Box borderTop="1px solid" borderColor="border.default" />
              }
            >
              {items.map((n) => {
                const link = linkForNotification(n);
                const hasLink = link && link !== "/notifications";
                return (
                  <Box
                    key={n.id}
                    px={4}
                    py={3}
                    cursor="pointer"
                    bg={n.is_read ? "surface.card" : "blue.50"}
                    _hover={{ bg: "gray.100" }}
                    onClick={() => onClickItem(n)}
                  >
                    <HStack justify="space-between" align="start">
                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight={n.is_read ? "normal" : "semibold"}
                        >
                          {n.message || n.type}
                        </Text>
                        <HStack spacing={2} mt={1}>
                          <Text fontSize="xs" color="text.muted">
                            {n.type}
                            {n.scope && n.team_key
                              ? ` · ${n.scope}/${n.team_key}`
                              : ""}
                            {" · "}
                            {n.created_at
                              ? new Date(n.created_at).toLocaleString()
                              : ""}
                          </Text>
                          {hasLink && (
                            <Text fontSize="xs" color="gwbi.500" fontWeight="medium">
                              {link.replace(/^\//, "").split("/")[0].replace(/-/g, " ")} →
                            </Text>
                          )}
                        </HStack>
                      </Box>
                      {!n.is_read && (
                        <Badge colorScheme="blue" fontSize="0.6em">
                          new
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
